// The postMessage protocol between the Aevo widget studio (the parent window)
// and the widget running in preview mode inside its iframe
// (`/widget/v1/preview`, which sets `AevoEchoConfig = { preview: true }`).
//
// Studio → widget:
//   { type: 'aevo-echo:preview-config', config }      apply a whole (or partial) WidgetConfig
//   { type: 'aevo-echo:preview-state', state }        show one state of the widget
//   { type: 'aevo-echo:preview-highlight', part }     outline one part (null clears)
//   { type: 'aevo-echo:preview-demo' }                play the scripted sample conversation
//
// Widget → studio:
//   { type: 'aevo-echo:preview-ready', version }      the widget and its panel are ready
//   { type: 'aevo-echo:preview-select', part }        the user clicked a part of the widget
//
// The widget only accepts messages from the parent window whose origin is in
// the allowlist (`AevoEchoConfig.previewOrigins`, or the origin of
// `document.referrer` when none is given), and only posts to those origins.
// This module is types and pure checks only, so the studio can copy it.

export const PREVIEW_MESSAGE_PREFIX = 'aevo-echo:' as const

/** Parts of the widget the studio can highlight and the user can click. */
export const PREVIEW_PARTS = [
  'launcher',
  'header',
  'banner',
  'greeting',
  'prompt',
  'composer',
  'pre-chat',
  'away',
] as const
export type PreviewPart = (typeof PREVIEW_PARTS)[number]

export const PREVIEW_STATES = ['closed', 'open', 'conversation', 'away', 'pre-chat'] as const
export type PreviewState = (typeof PREVIEW_STATES)[number]

export interface PreviewConfigMessage {
  type: 'aevo-echo:preview-config'
  /** A WidgetConfig (any subset; defaults fill the rest). */
  config: unknown
}

export interface PreviewStateMessage {
  type: 'aevo-echo:preview-state'
  state: PreviewState
}

export interface PreviewHighlightMessage {
  type: 'aevo-echo:preview-highlight'
  /** `null` clears the highlight. */
  part: PreviewPart | null
}

export interface PreviewDemoMessage {
  type: 'aevo-echo:preview-demo'
}

export type PreviewInboundMessage =
  | PreviewConfigMessage
  | PreviewStateMessage
  | PreviewHighlightMessage
  | PreviewDemoMessage

export interface PreviewReadyMessage {
  type: 'aevo-echo:preview-ready'
  version: string
}

export interface PreviewSelectMessage {
  type: 'aevo-echo:preview-select'
  part: PreviewPart
  /** Index of the clicked prompt chip, for `part: 'prompt'`. */
  index?: number
}

export type PreviewOutboundMessage = PreviewReadyMessage | PreviewSelectMessage

export function isPreviewPart(value: unknown): value is PreviewPart {
  return (PREVIEW_PARTS as readonly unknown[]).includes(value)
}

export function isPreviewState(value: unknown): value is PreviewState {
  return (PREVIEW_STATES as readonly unknown[]).includes(value)
}

/** Narrow an incoming `event.data` to a known studio message, or null. */
export function parsePreviewMessage(data: unknown): PreviewInboundMessage | null {
  if (!data || typeof data !== 'object') return null
  const msg = data as Record<string, unknown>
  switch (msg.type) {
    case 'aevo-echo:preview-config':
      return msg.config && typeof msg.config === 'object'
        ? { type: msg.type, config: msg.config }
        : null
    case 'aevo-echo:preview-state':
      return isPreviewState(msg.state) ? { type: msg.type, state: msg.state } : null
    case 'aevo-echo:preview-highlight':
      return msg.part === null || isPreviewPart(msg.part)
        ? { type: msg.type, part: (msg.part as PreviewPart | null) ?? null }
        : null
    case 'aevo-echo:preview-demo':
      return { type: msg.type }
    default:
      return null
  }
}

/** The serialised origin of a URL, or null for anything that isn't http(s). */
export function originOf(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
    return parsed.origin
  } catch {
    return null
  }
}

/**
 * The origins a preview may talk to: the explicit list when one is given
 * (entries that aren't http(s) origins are dropped), else the referrer's
 * origin, else none — in which case the preview ignores every message.
 */
export function resolvePreviewOrigins(explicit: unknown, referrer: string | null | undefined): string[] {
  if (Array.isArray(explicit)) {
    const list = explicit
      .map((entry) => (typeof entry === 'string' ? originOf(entry) : null))
      .filter((o): o is string => o !== null)
    return Array.from(new Set(list))
  }
  const fromReferrer = originOf(referrer)
  return fromReferrer ? [fromReferrer] : []
}

export function isAllowedPreviewOrigin(origin: string, allowed: readonly string[]): boolean {
  return allowed.includes(origin)
}
